import { useState, useEffect, useCallback } from "react";
import { createCustNote, deleteCustNote, fetchCustomerInfo, updateCustNote } from "../../Services/MainService";

// ─── ICONS ───────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);
const EditIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const SearchIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const CloseIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const NoteIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0119 9.414V19a2 2 0 01-2 2z" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

// ─── TOAST ────────────────────────────────────────────────────────
function Toast({ toasts }) {
  return (
    <div style={{ position: "fixed", bottom: 28, right: 28, zIndex: 500, display: "flex", flexDirection: "column", gap: 10 }}>
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            background: "#1A1A18",
            color: "#fff",
            padding: "13px 20px",
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 10,
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            animation: "toastIn 0.25s ease",
            maxWidth: 320,
          }}
        >
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.type === "success" ? "#4ADE80" : "#E85555", flexShrink: 0 }} />
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ─── NOTE CARD ───────────────────────────────────────────────────
function NoteCard({ note, index, onEdit, onDelete }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#FFFFFF",
        border: "1px solid #E8E8E4",
        borderRadius: 16,
        padding: "20px 24px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        boxShadow: hovered ? "0 8px 32px rgba(0,0,0,0.12)" : "0 2px 12px rgba(0,0,0,0.07)",
        transform: hovered ? "translateY(-1px)" : "translateY(0)",
        transition: "all 0.2s",
        animation: "slideIn 0.3s ease",
      }}
    >
      <div style={{
        width: 32, height: 32,
        borderRadius: 8,
        background: "#F7F7F5",
        border: "1px solid #E8E8E4",
        display: "grid", placeItems: "center",
        fontSize: 13, fontWeight: 600,
        color: "#7A7A72",
        flexShrink: 0,
      }}>
        {index + 1}
      </div>
      <div style={{ flex: 1, fontSize: 15, lineHeight: 1.5, color: "#1A1A18", wordBreak: "break-word" }}>
        {note.note}
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <IconBtn type="edit" onClick={() => onEdit(note)} title="Edit">
          <EditIcon />
        </IconBtn>
        <IconBtn type="delete" onClick={() => onDelete(note.id)} title="Delete">
          <TrashIcon />
        </IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ type, onClick, title, children }) {
  const [hovered, setHovered] = useState(false);
  const hoverStyle = type === "edit"
    ? { background: "#FFF8D6", border: "1px solid #F5C518", color: "#d4a900" }
    : { background: "#FFF0F0", border: "1px solid #E85555", color: "#E85555" };

  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 38, height: 38,
        border: "1px solid #E8E8E4",
        borderRadius: 10,
        background: "#F7F7F5",
        cursor: "pointer",
        display: "grid", placeItems: "center",
        color: "#7A7A72",
        transition: "all 0.18s",
        ...(hovered ? hoverStyle : {}),
      }}
    >
      {children}
    </button>
  );
}

// ─── MODAL ────────────────────────────────────────────────────────
function Modal({ open, title, onClose, children }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (open) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(4px)",
        zIndex: 200,
        display: "grid", placeItems: "center",
        animation: "fadeIn 0.2s ease",
      }}
    >
      <div style={{
        background: "#FFFFFF",
        borderRadius: 20,
        padding: 32,
        width: 480,
        maxWidth: "92vw",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        animation: "scaleIn 0.22s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, fontWeight: 400 }}>{title}</h2>
          <button onClick={onClose} style={{
            width: 32, height: 32, border: "none",
            background: "#F7F7F5", borderRadius: 8,
            cursor: "pointer", display: "grid", placeItems: "center",
            color: "#7A7A72",
          }}>
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── SKELETON ────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div style={{
      background: "#FFFFFF", border: "1px solid #E8E8E4",
      borderRadius: 16, height: 72,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.04) 50%, transparent 100%)",
        animation: "shimmer 1.4s infinite",
      }} />
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────
export default function CustomerNotes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toasts, setToasts] = useState([]);

  // Add/Edit Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  }, []);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCustomerInfo();
      setNotes(data.customer_note || []);
    } catch {
      showToast("Failed to load notes. Please refresh the page.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const openAddModal = () => {
    setEditingNote(null);
    setNoteText("");
    setModalOpen(true);
  };

  const openEditModal = (note) => {
    setEditingNote(note);
    setNoteText(note.note);
    setModalOpen(true);
  };

  const openDeleteModal = (id) => {
    setDeletingId(id);
    setDeleteModalOpen(true);
  };

  const handleSave = async () => {
    if (!noteText.trim()) { showToast("Please enter a note.", "error"); return; }
    setSaving(true);
    try {
      if (editingNote) {
        const updated = await updateCustNote(editingNote.id, noteText.trim());
        setNotes((prev) => prev.map((n) => n.id === editingNote.id ? { ...n, note: noteText.trim(), ...updated } : n));
        showToast("Note updated successfully");
      } else {
        const created = await createCustNote(noteText.trim());
        setNotes((prev) => [...prev, {id: created.id, note: noteText.trim()}]);
        showToast("Note added successfully");
      }
      setModalOpen(false);
    } catch {
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCustNote(deletingId);
      setNotes((prev) => prev.filter((n) => n.id !== deletingId));
      showToast("Note deleted");
      setDeleteModalOpen(false);
    } catch {
      showToast("Failed to delete note.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = notes.filter((n) => n.note.toLowerCase().includes(search.toLowerCase()));
  const lastUpdated = notes.length ? new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.96) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        textarea { font-family: 'DM Sans', sans-serif; }
        input { font-family: 'DM Sans', sans-serif; }
      `}</style>

      <div style={{ fontFamily: "'DM Sans', sans-serif", background: "#F7F7F5", minHeight: "100vh" }}>
        {/* TOPBAR */}

        {/* MAIN */}
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "48px 24px" }}>

          {/* PAGE HEADER */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 40, gap: 20 }}>
            <div>
              <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 36, fontWeight: 400, lineHeight: 1.1, marginBottom: 8 }}>Order Notes</h1>
              <p style={{ color: "#7A7A72", fontSize: 15, maxWidth: 420, lineHeight: 1.6 }}>
                Got special instructions for your laundry order? Add, edit, or organize your saved instructions to ensure every order is handled exactly your way.
              </p>
            </div>
            <AddButton onClick={openAddModal} />
          </div>

          {/* STATS */}
          <div style={{ display: "flex", gap: 16, marginBottom: 32 }}>
            <StatCard icon={<NoteIcon />} label="Total Notes" value={loading ? "—" : notes.length} />
            <StatCard icon={<CalendarIcon />} label="Last Updated" value={loading ? "—" : lastUpdated} small />
          </div>

          {/* SEARCH */}
          <div style={{ position: "relative", marginBottom: 20 }}>
            <div style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#7A7A72" }}>
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", padding: "13px 16px 13px 46px",
                border: "1px solid #E8E8E4", borderRadius: 12,
                fontSize: 15, background: "#FFFFFF", outline: "none",
                transition: "border 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#F5C518")}
              onBlur={(e) => (e.target.style.borderColor = "#E8E8E4")}
            />
          </div>

          {/* NOTES LIST */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading ? (
              [1, 2, 3].map((i) => <Skeleton key={i} />)
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 20px" }}>
                <div style={{ width: 72, height: 72, background: "#FFFFFF", border: "1px solid #E8E8E4", borderRadius: 20, display: "grid", placeItems: "center", margin: "0 auto 20px", color: "#7A7A72" }}>
                  <NoteIcon />
                </div>
                <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, marginBottom: 8 }}>No notes yet</h3>
                <p style={{ color: "#7A7A72", fontSize: 15 }}>Add your first order note to get started.</p>
              </div>
            ) : (
              filtered.map((note, i) => (
                <NoteCard key={note.id} note={note} index={i} onEdit={openEditModal} onDelete={openDeleteModal} />
              ))
            )}
          </div>
        </div>

        {/* ADD / EDIT MODAL */}
        <Modal open={modalOpen} title={editingNote ? "Edit Note" : "Add Note"} onClose={() => setModalOpen(false)}>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value.slice(0, 500))}
            placeholder="Write your order instruction here…"
            rows={5}
            style={{
              width: "100%", border: "1px solid #E8E8E4", borderRadius: 12,
              padding: "14px 16px", fontSize: 15, resize: "vertical",
              outline: "none", color: "#1A1A18", background: "#F7F7F5",
              transition: "border 0.2s",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#F5C518"; e.target.style.background = "#fff"; }}
            onBlur={(e) => { e.target.style.borderColor = "#E8E8E4"; e.target.style.background = "#F7F7F5"; }}
          />
          <div style={{ textAlign: "right", fontSize: 12, color: "#7A7A72", marginTop: 6 }}>{noteText.length} / 500</div>
          <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
            <button onClick={() => setModalOpen(false)} style={{
              flex: 1, padding: 13, border: "1px solid #E8E8E4", borderRadius: 12,
              background: "#F7F7F5", fontSize: 15, fontWeight: 500, cursor: "pointer", color: "#1A1A18",
            }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{
              flex: 2, padding: 13, border: "none", borderRadius: 12,
              background: saving ? "#d4a900" : "#F5C518", fontSize: 15, fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer", color: "#000", transition: "0.2s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              {saving ? (
                <div style={{ width: 16, height: 16, border: "2px solid rgba(0,0,0,0.2)", borderTopColor: "#000", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />
              ) : (
                editingNote ? "Update Note" : "Save Note"
              )}
            </button>
          </div>
        </Modal>

        {/* DELETE MODAL */}
        <Modal open={deleteModalOpen} title="" onClose={() => setDeleteModalOpen(false)}>
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 56, background: "#FFF0F0", borderRadius: 16, display: "grid", placeItems: "center", margin: "0 auto 16px", color: "#E85555" }}>
              <TrashIcon />
            </div>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, fontWeight: 400 }}>Delete Note?</h2>
            <p style={{ color: "#7A7A72", fontSize: 15, marginTop: 8, lineHeight: 1.6 }}>
              This will permanently remove the note from your account. This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <button onClick={() => setDeleteModalOpen(false)} style={{
                flex: 1, padding: 13, border: "1px solid #E8E8E4", borderRadius: 12,
                background: "#F7F7F5", fontSize: 15, fontWeight: 500, cursor: "pointer", color: "#1A1A18",
              }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{
                flex: 2, padding: 13, border: "none", borderRadius: 12,
                background: "#E85555", fontSize: 15, fontWeight: 600,
                cursor: deleting ? "not-allowed" : "pointer", color: "#fff", transition: "0.2s",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </Modal>

        <Toast toasts={toasts} />
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// ─── SUB COMPONENTS ───────────────────────────────────────────────
function AddButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        background: "#F5C518", color: "#000",
        border: "none", borderRadius: 12,
        padding: "12px 22px",
        fontSize: 15, fontWeight: 600, cursor: "pointer",
        whiteSpace: "nowrap", flexShrink: 0,
        transition: "all 0.2s",
        transform: hovered ? "translateY(-1px)" : "translateY(0)",
        boxShadow: hovered ? "0 4px 16px rgba(245,197,24,0.35)" : "none",
        background: hovered ? "#d4a900" : "#F5C518",
      }}
    >
      <PlusIcon /> Add Note
    </button>
  );
}

function StatCard({ icon, label, value, small }) {
  return (
    <div style={{
      background: "#FFFFFF", border: "1px solid #E8E8E4",
      borderRadius: 12, padding: "16px 24px",
      flex: 1, display: "flex", alignItems: "center", gap: 12,
    }}>
      <div style={{ width: 40, height: 40, background: "#FFF8D6", borderRadius: 10, display: "grid", placeItems: "center", color: "#d4a900" }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#7A7A72", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
        <div style={{ fontSize: small ? 16 : 24, fontFamily: "'DM Serif Display', serif", marginTop: small ? 2 : 0 }}>{value}</div>
      </div>
    </div>
  );
}